import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import Glyph from "./navGlyphs";

/*
 * MEETINGS ▸ AVAILABILITY — the one set of rules for offering times.
 *
 * Built against Gabriel's mockup (Availability.dc.html). Everything that
 * proposes a meeting reads from here: the after-hours reply, the no-show rescue
 * and the booking page. It replaces the Calendar tab each of those used to
 * carry, which is why the page says, before anything is saved, which of them a
 * change reaches.
 *
 * THE RIGHT-HAND PANEL IS THE AUTOMATION, ASKED A QUESTION. It posts the rules
 * as they stand on screen, unsaved, and the server runs the same slot finder
 * and the same availability sentence the email uses. For a country that spans
 * several zones — the US is one — the email names no exact times, so the panel
 * shows the sentence rather than a list the email would never send.
 *
 * Departures from the mockup, all toward the live data:
 *   - Buffer offers 30 min. The live setting is 30, and a segmented control
 *     without its current value would silently change it on first click.
 *   - One US sample, not east and west. The CRM maps a country to one zone, so
 *     a Californian and a New Yorker get the same email; two buttons would
 *     promise a difference that does not exist. Israel is added, because Sunday
 *     is offered only to Israeli leads and that is worth being able to see.
 *   - No per-reason skip counts under the slots: the slot finder does not
 *     report them, and inventing them here would be a second implementation.
 */

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const LENGTHS = [15, 20, 30, 45, 60];
const BUFFERS = [0, 5, 10, 15, 30];
const SAMPLES = [
  { country: "United States", label: "US" },
  { country: "United Kingdom", label: "UK" },
  { country: "Germany", label: "Germany" },
  { country: "Australia", label: "Australia" },
  { country: "Israel", label: "Israel" },
];

// Half-hour steps across the day, for the window and US-band selects.
const TIMES = (() => {
  const out = [];
  for (let m = 6 * 60; m <= 23 * 60; m += 30) {
    out.push(`${String(Math.floor(m / 60)).padStart(2, "0")}:${m % 60 ? "30" : "00"}`);
  }
  return out;
})();

const NUMBER_WORDS = ["zero", "one", "two", "three", "four", "five"];

// A select must always be able to show its current value, even one set before
// the half-hour grid existed (a window opening at 09:15, say).
function timeOptions(current) {
  return current && !TIMES.includes(current) ? [...TIMES, current].sort() : TIMES;
}

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function joinNames(names) {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/* ---------------------------------------------------------------- pieces */

function Card({ title, sub, children }) {
  return (
    <section className="crm-avail__card">
      {(title || sub) && (
        <div className="crm-avail__cardhead">
          {title && <h3>{title}</h3>}
          {sub && <span>{sub}</span>}
        </div>
      )}
      {children}
    </section>
  );
}

function Toggle({ on, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className={`crm-avail__toggle${on ? " is-on" : ""}`}
      onClick={() => onChange(!on)}
    >
      <span />
    </button>
  );
}

function Segmented({ options, value, onChange, unit }) {
  return (
    <div className="crm-avail__seg" role="radiogroup">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={option === value}
          className={option === value ? "is-on" : ""}
          onClick={() => onChange(option)}
        >
          {option} {unit}
        </button>
      ))}
    </div>
  );
}

function Stepper({ label, help, value, min, max, format, onChange }) {
  return (
    <div className="crm-avail__row">
      <div className="crm-avail__rowtext">
        <span>{label}</span>
        <span className="crm-avail__help">{help}</span>
      </div>
      <div className="crm-avail__stepper">
        <button type="button" aria-label={`Less ${label.toLowerCase()}`} onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}>
          −
        </button>
        <span className="crm-num">{format(value)}</span>
        <button type="button" aria-label={`More ${label.toLowerCase()}`} onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max}>
          +
        </button>
      </div>
    </div>
  );
}

// `allowOff` adds an empty choice, for the US band's latest time: empty turns
// the band off, and a select that could not show that would claim a time.
function TimeSelect({ value, onChange, label, allowOff = false }) {
  return (
    <select className="crm-avail__time crm-num" value={value || ""} onChange={(e) => onChange(e.target.value)} aria-label={label}>
      {allowOff && <option value="">Off</option>}
      {timeOptions(value).map((t) => (
        <option key={t} value={t}>
          {t}
        </option>
      ))}
    </select>
  );
}

/* ------------------------------------------------------- the busy calendars */

/*
 * Google names the primary calendar two ways — the alias "primary" and its own
 * id, the address — and the live list holds both. So the primary row is ticked
 * when either is present, and unticking it removes both; otherwise the box
 * would clear and the calendar would go on blocking slots.
 */
function BusyCalendars({ status, ids, onChange }) {
  if (!status) return <p className="crm-avail__help crm-avail__pad">Reading your Google calendars…</p>;
  if (!status.connected) {
    return (
      <div className="crm-avail__warn crm-avail__pad">
        <b>Google Calendar is not connected.</b>
        <span>
          {status.error ? `Google answered: ${status.error}. ` : ""}Until it is, nothing that proposes a meeting can check
          when you are free, so no times go out.
        </span>
        {/* The auth route needs the dashboard token in the address since the
            login hardening (#124): a plain link lands on a 401. */}
        <a
          className="crm-btn crm-btn--dark"
          href={`/api/gmail/auth?redirect=1&token=${encodeURIComponent(localStorage.getItem("dashboard_token") || "")}`}
        >
          Reconnect Google
        </a>
      </div>
    );
  }

  const isOn = (entry) => ids.includes(entry.id) || (entry.primary && ids.includes("primary"));
  const toggle = (entry) => {
    if (isOn(entry)) {
      onChange(ids.filter((id) => id !== entry.id && !(entry.primary && id === "primary")));
    } else {
      onChange([...ids, entry.id]);
    }
  };
  const known = new Set(status.calendars.map((c) => c.id));
  const primary = status.calendars.find((c) => c.primary);
  // Ids in the saved list that Google no longer returns — a calendar deleted or
  // unshared. They block nothing, and are shown so they can be cleared.
  const orphans = ids.filter((id) => !known.has(id) && !(id === "primary" && primary));

  return (
    <div className="crm-avail__list">
      {status.calendars.map((entry) => (
        <label key={entry.id} className="crm-avail__check">
          <input type="checkbox" checked={isOn(entry)} onChange={() => toggle(entry)} />
          <i style={{ background: entry.background_color || "#5C5A54" }} />
          <span className="crm-avail__checkname">{entry.name}</span>
          <span className="crm-avail__help">
            {entry.primary ? "Primary" : entry.access_role === "owner" ? "Yours" : entry.access_role === "reader" ? "Subscribed" : "Shared"}
          </span>
        </label>
      ))}
      {orphans.map((id) => (
        <label key={id} className="crm-avail__check">
          <input type="checkbox" checked onChange={() => onChange(ids.filter((x) => x !== id))} />
          <i style={{ background: "#C9C6BB" }} />
          <span className="crm-avail__checkname">{id}</span>
          <span className="crm-avail__help">No longer in your Google list</span>
        </label>
      ))}
      {!status.calendars.length && <p className="crm-avail__help">No calendars came back from Google.</p>}
    </div>
  );
}

/* ------------------------------------------------------------ the preview */

function Preview({ draft, dirty, connected }) {
  const [country, setCountry] = useState(SAMPLES[0].country);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!draft) return undefined;
    setLoading(true);
    const timer = setTimeout(() => {
      api("/availability/preview", { method: "POST", body: JSON.stringify({ availability: draft, country }) })
        .then(setResult)
        .catch((err) => setResult({ errors: [err.message] }))
        .finally(() => setLoading(false));
    }, 400);
    return () => clearTimeout(timer);
  }, [draft, country]);

  const mine = (iso) =>
    new Date(iso).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: result?.timezone || "Asia/Jerusalem",
    });

  return (
    <section className={`crm-avail__card${loading ? " is-loading" : ""}`}>
      <div className="crm-avail__cardhead crm-avail__cardhead--stack">
        <span className="crm-avail__eyebrow">What a lead would get right now</span>
        <span>Live calendar · settings as on screen{dirty ? " (unsaved)" : ""}</span>
      </div>
      <div className="crm-avail__pills">
        {SAMPLES.map((s) => (
          <button
            key={s.country}
            type="button"
            className={s.country === country ? "is-on" : ""}
            onClick={() => setCountry(s.country)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="crm-avail__offer">
        {!result ? (
          <p className="crm-avail__help">Reading your calendar…</p>
        ) : result.errors?.length ? (
          <p className="crm-avail__bad">{result.errors.join(" ")}</p>
        ) : result.calendar_error ? (
          <p className="crm-avail__bad">
            Your calendar did not answer: {result.calendar_error}. A lead arriving now would get no times — we do not
            offer hours we could not check.
          </p>
        ) : (
          <>
            {result.exact_times && result.slots.length > 0 && (
              <ol className="crm-avail__slots">
                {result.slots.map((slot, i) => (
                  <li key={slot.start}>
                    <span className="crm-avail__n crm-num">{i + 1}</span>
                    <b>{slot.label}</b>
                    <span className="crm-avail__mine crm-num">{mine(slot.start)} you</span>
                  </li>
                ))}
              </ol>
            )}
            {!result.slots.length && (
              <p className="crm-avail__bad">
                <Glyph name="calendar" size={15} />
                {connected === false
                  ? "No times, because Google Calendar is not connected — nothing can be checked. This is the sentence a lead would get right now."
                  : "No times fit. The email names days instead of times."}
              </p>
            )}
            <div className="crm-avail__sentence">
              <span className="crm-avail__eyebrow">The email says</span>
              <p>{result.sentence}</p>
            </div>
            <p className="crm-avail__help">
              {result.exact_times
                ? `Exact times, in the lead's clock (${result.lead_timezone}).`
                : result.lead_timezone
                ? `${country === "United States" ? "The US spans several zones" : "This country spans several zones"}, so the email describes a window instead of naming times, with the zone it assumes (${result.lead_timezone}) said out loud.`
                : "We cannot place this country in a zone, so the email names Israel time outright."}
            </p>
          </>
        )}
      </div>
    </section>
  );
}

/* ---------------------------------------------------- quick availability */

/*
 * Three times as a block to paste into a message he writes himself. Same slot
 * finder as the panel below, with the rules as they stand on screen.
 */
const QUICK_CHOICES = ["US", "CET", "UK", "Other"];
const US_ZONE_CHOICES = ["PT", "MT", "CT", "ET"];

function QuickAvailability({ draft, countries }) {
  const [choice, setChoice] = useState("US");
  const [usZone, setUsZone] = useState("ET");
  const [country, setCountry] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!draft || (choice === "Other" && !country)) return undefined;
    setLoading(true);
    const timer = setTimeout(() => {
      api("/availability/quick", {
        method: "POST",
        body: JSON.stringify({ availability: draft, choice, us_zone: usZone, country }),
      })
        .then(setResult)
        .catch((err) => setResult({ errors: [err.message] }))
        .finally(() => setLoading(false));
    }, 400);
    return () => clearTimeout(timer);
  }, [draft, choice, usZone, country]);

  const copy = () => {
    navigator.clipboard.writeText(result.text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <section className={`crm-avail__card${loading ? " is-loading" : ""}`}>
      <div className="crm-avail__cardhead crm-avail__cardhead--stack">
        <span className="crm-avail__eyebrow">Quick availability</span>
        <span>Three open times to paste into a message.</span>
      </div>
      <div className="crm-avail__pills">
        {QUICK_CHOICES.map((c) => (
          <button key={c} type="button" className={c === choice ? "is-on" : ""} onClick={() => setChoice(c)}>
            {c}
          </button>
        ))}
        {choice === "US" && (
          <select className="crm-avail__time" value={usZone} onChange={(e) => setUsZone(e.target.value)} aria-label="US time zone">
            {US_ZONE_CHOICES.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        )}
        {choice === "Other" && (
          <select className="crm-avail__time" value={country} onChange={(e) => setCountry(e.target.value)} aria-label="Country">
            <option value="">Pick a country…</option>
            {(countries || []).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="crm-avail__offer">
        {choice === "Other" && !country ? (
          <p className="crm-avail__help">Pick a country.</p>
        ) : !result ? (
          <p className="crm-avail__help">Reading your calendar…</p>
        ) : result.errors?.length ? (
          <p className="crm-avail__bad">{result.errors.join(" ")}</p>
        ) : !result.text ? (
          <p className="crm-avail__bad">No times fit in the look-ahead window.</p>
        ) : (
          <>
            <QuickBlock text={result.text} myTimes={result.my_times} />
            <button type="button" className="crm-btn crm-btn--dark crm-avail__copy" onClick={copy}>
              {copied ? "Copied" : "Copy"}
            </button>
          </>
        )}
      </div>
    </section>
  );
}

/*
 * The block as it pastes, with his own (Israel) time in grey beside each line.
 * The grey times are never selected or copied: Copy sends result.text alone.
 */
function QuickBlock({ text, myTimes }) {
  if (!myTimes?.length) return <pre className="crm-avail__quick">{text}</pre>;
  let bullet = 0;
  return (
    <div className="crm-avail__quick crm-avail__quick--mine">
      {text.split("\n").map((line, i) => {
        if (!line.startsWith("* ")) return <div key={i}>{line || "\u00a0"}</div>;
        // "Thu Oct 22, 18:00" -> "Thu 18:00": the weekday shows a day that crosses midnight.
        const mine = (myTimes[bullet++] || "").replace(/^(\w{3}) \w{3} \d+, /, "$1 ");
        return (
          <div key={i} className="crm-avail__quickrow">
            <span>{line}</span>
            {mine && <span className="crm-avail__mine" title="Your time (Israel)">{mine} IL</span>}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------- used by */

function UsedBy({ usedBy }) {
  if (!usedBy) return null;
  const rows = [
    ...usedBy.automations.map((a) => ({
      key: `a${a.id}`,
      name: a.name,
      where: a.type === "call_no_show" ? "Meetings ▸ Automations" : "Inbound ▸ Automations",
      what: a.type === "call_no_show" ? "offers new times after an empty room" : "offers times in the first reply",
      state: !a.enabled ? "Off" : a.dry_run ? "Dry run" : "On",
      to: `/automations/${a.id}`,
    })),
    {
      key: "booking",
      name: "Booking page",
      where: "Meetings ▸ Booking page",
      what: `shows open slots, ${usedBy.booking_page.lookahead_business_days} business days ahead`,
      state: usedBy.booking_page.enabled ? "On" : "Off",
      to: "/booking-page?tab=page",
    },
  ];
  return (
    <section className="crm-avail__card">
      <div className="crm-avail__cardhead crm-avail__cardhead--stack">
        <span className="crm-avail__eyebrow">Used by</span>
        <span>A change here changes all of these.</span>
      </div>
      <div className="crm-avail__used">
        {rows.map((row) => (
          <Link key={row.key} to={row.to} className="crm-avail__useditem">
            <div>
              <b>{row.name}</b>
              <span>
                {row.where} · {row.what}
              </span>
            </div>
            <span className={`crm-avail__state crm-avail__state--${row.state === "On" ? "on" : "off"}`}>{row.state}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------- the page */

export default function AvailabilityPage() {
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [usedBy, setUsedBy] = useState(null);
  const [timezone, setTimezone] = useState("Asia/Jerusalem");
  const [countries, setCountries] = useState([]);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api("/availability")
      .then((data) => {
        setSaved(data.availability);
        setDraft(clone(data.availability));
        setUsedBy(data.used_by);
        setTimezone(data.timezone);
        setCountries(data.countries || []);
      })
      .catch((err) => setError(err.message));
    api("/automations/calendar/status")
      .then(setStatus)
      .catch((err) => setStatus({ connected: false, error: err.message, calendars: [] }));
  }, []);

  const dirty = useMemo(() => !!saved && !!draft && JSON.stringify(saved) !== JSON.stringify(draft), [saved, draft]);
  const set = useCallback((patch) => setDraft((current) => ({ ...current, ...patch })), []);

  // What a save reaches, named, for the bar — everything that reads these
  // rules, on or off, because an automation switched off today reads them the
  // moment it is switched back on.
  const reached = usedBy
    ? [...usedBy.automations.map((a) => a.name), "the booking page"]
    : [];

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = await api("/availability", { method: "PUT", body: JSON.stringify({ availability: draft }) });
      setSaved(data.availability);
      setDraft(clone(data.availability));
      setUsedBy(data.used_by);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!draft) {
    return (
      <div className="crm-avail">
        <p className="crm-avail__help">{error || "Loading your availability…"}</p>
      </div>
    );
  }

  const setWindow = (day, index, value) => {
    const next = draft.bookable_window.map((w) => (w ? [...w] : null));
    next[day][index] = value;
    set({ bookable_window: next });
  };
  const toggleDay = (day, on) => {
    const next = draft.bookable_window.map((w) => (w ? [...w] : null));
    next[day] = on ? ["10:00", "18:00"] : null;
    set({ bookable_window: next });
  };
  const zoneName = timezone === "Asia/Jerusalem" ? "Israel time" : timezone.replace("_", " ");

  return (
    <div className="crm-avail">
      {dirty && (
        <div className="crm-avail__bar">
          <span>
            <b>Unsaved changes.</b> Saving changes the times offered by {joinNames(reached)}.
          </span>
          <button type="button" className="crm-avail__barbtn" onClick={() => setDraft(clone(saved))} disabled={saving}>
            Discard
          </button>
          <button type="button" className="crm-avail__barbtn crm-avail__barbtn--save" onClick={save} disabled={saving}>
            {saving
              ? "Saving…"
              : reached.length === 2
              ? "Save for both"
              : reached.length > 2
              ? `Save for all ${NUMBER_WORDS[reached.length] || reached.length}`
              : "Save"}
          </button>
        </div>
      )}

      <div className="crm-avail__body">
        <header className="crm-avail__head">
          <div className="crm-avail__crumb">
            <span>Meetings</span>
            <Glyph name="chevronRight" size={12} />
            <span>Availability</span>
          </div>
          <div className="crm-avail__titlerow">
            <h1>Availability</h1>
            <Link to="/meetings/calendar" className="crm-avail__back">
              <Glyph name="calendar" size={15} /> Back to calendar
            </Link>
          </div>
          <p>The one set of rules for offering times. Everything that proposes a meeting reads from here.</p>
        </header>

        {error && <p className="crm-avail__bad">{error}</p>}

        <div className="crm-avail__cols">
          <div className="crm-avail__main">
            <Card title="Calendars that count as busy" sub={`Events on these block a slot.${status?.email ? ` From Google, ${status.email}.` : ""}`}>
              <BusyCalendars status={status} ids={draft.busy_calendar_ids} onChange={(busy_calendar_ids) => set({ busy_calendar_ids })} />
            </Card>

            <Card title="Meeting">
              <div className="crm-avail__pair">
                <div className="crm-avail__field">
                  <span>Length</span>
                  <Segmented options={LENGTHS.includes(draft.meeting_minutes) ? LENGTHS : [...LENGTHS, draft.meeting_minutes].sort((a, b) => a - b)} value={draft.meeting_minutes} unit="min" onChange={(meeting_minutes) => set({ meeting_minutes })} />
                </div>
                <div className="crm-avail__field">
                  <span>Buffer between meetings</span>
                  <Segmented options={BUFFERS.includes(draft.buffer_minutes) ? BUFFERS : [...BUFFERS, draft.buffer_minutes].sort((a, b) => a - b)} value={draft.buffer_minutes} unit="min" onChange={(buffer_minutes) => set({ buffer_minutes })} />
                </div>
              </div>
            </Card>

            <Card title="Bookable window" sub={`Your hours, ${zoneName}. Sunday is only ever offered to leads in Israel.`}>
              <div className="crm-avail__list">
                {DAY_NAMES.map((name, day) => {
                  const window = draft.bookable_window[day];
                  return (
                    <div key={name} className="crm-avail__day">
                      <Toggle on={!!window} onChange={(on) => toggleDay(day, on)} label={`Offer ${name}`} />
                      <span className="crm-avail__dayname">{name}</span>
                      {window ? (
                        <span className="crm-avail__times">
                          <TimeSelect value={window[0]} onChange={(v) => setWindow(day, 0, v)} label={`${name} opens`} />
                          <span className="crm-avail__help">to</span>
                          <TimeSelect value={window[1]} onChange={(v) => setWindow(day, 1, v)} label={`${name} closes`} />
                        </span>
                      ) : (
                        <span className="crm-avail__off">Off</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card title="What gets offered">
              <div className="crm-avail__list">
                <Stepper label="Slots to offer" help="How many times go into a reply." value={draft.slots_to_offer} min={1} max={10} format={(v) => `${v} ${v === 1 ? "slot" : "slots"}`} onChange={(slots_to_offer) => set({ slots_to_offer })} />
                <Stepper label="Look ahead" help="Business days, starting today. The booking page keeps its own." value={draft.lookahead_business_days} min={1} max={30} format={(v) => `${v} days`} onChange={(lookahead_business_days) => set({ lookahead_business_days })} />
                <Stepper label="Minimum notice" help="No slot sooner than this from now." value={draft.min_notice_hours} min={0} max={168} format={(v) => `${v} h`} onChange={(min_notice_hours) => set({ min_notice_hours })} />
                <div className="crm-avail__row">
                  <div className="crm-avail__rowtext">
                    <span>Spread across days</span>
                    <span className="crm-avail__help">One slot per day before doubling up on any day.</span>
                  </div>
                  <Toggle on={draft.spread_across_days} onChange={(spread_across_days) => set({ spread_across_days })} label="Spread across days" />
                </div>
              </div>
            </Card>

            <Card title="US band" sub="For US leads, the window stretches into your evening so one time is not the crack of dawn their side.">
              <div className="crm-avail__pair">
                <label className="crm-avail__field">
                  <span>Latest, your time</span>
                  <TimeSelect value={draft.us_late_until} allowOff onChange={(us_late_until) => set({ us_late_until })} label="Latest time for a US lead, your time" />
                </label>
                <label className="crm-avail__field">
                  <span>Earliest, lead's own clock</span>
                  <TimeSelect value={draft.us_earliest_local || "09:00"} onChange={(us_earliest_local) => set({ us_earliest_local })} label="Earliest time for a US lead, their time" />
                </label>
              </div>
            </Card>
          </div>

          <aside className="crm-avail__side">
            <QuickAvailability draft={draft} countries={countries} />
            <Preview draft={draft} dirty={dirty} connected={status ? !!status.connected : null} />
            <UsedBy usedBy={usedBy} />
          </aside>
        </div>
      </div>
    </div>
  );
}
